import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import baseApi from '@/api/client';
import { CAPABILITIES_API } from '@/api/endpoints';
import { useIamUser } from '@/auth/iam';
import { useIsAuthenticated } from '@/auth/useIsAuthenticated';

// ---------------------------------------------------------------------------
// Capabilities are the backend's feature gates (`features` / `feature_grants`
// tables), resolved server-side per caller and mirrored here so the UI can hide
// nav items and disable controls that would only ever 403. The API re-checks
// every gated endpoint for itself — nothing here grants anything.

/**
 * The gated feature keys this app knows. Mirrors the backend's `FeatureKeys`:
 * `invoices` is the module (WeFact invoices, clients, mappings);
 * `oda` and `helpdesk` are those modules — switched off in production for now;
 * `session-schedules` is the timed remote start/stop module.
 */
export type FeatureKey = 'invoices' | 'oda' | 'helpdesk' | 'session-schedules';

interface CapabilitiesResponse {
  features: string[];
}

/** The signed-in caller's allowed feature keys. */
export function useCapabilities(): UseQueryResult<CapabilitiesResponse> {
  const isAuthenticated = useIsAuthenticated();
  // Scoped to the IAM identity for the same reason as `useIamUser`'s key: a
  // previous user's capabilities must never be served after a logout→login on the
  // same QueryClient. Waiting for the IAM profile also matches what the answer
  // means — grants are keyed by the caller's IAM email.
  const { data: iamUser } = useIamUser();
  const iamUserId = iamUser?.user.id ?? '';
  return useQuery({
    queryKey: ['me', 'capabilities', iamUserId],
    queryFn: async () => {
      const { data } = await baseApi.get<CapabilitiesResponse>(CAPABILITIES_API.ME);
      return data;
    },
    enabled: isAuthenticated && iamUserId !== '',
    staleTime: 5 * 60_000,
  });
}
